import {Accordion, Alert, Button, FormControl, FormGroup, Modal, Spinner, Tab, Tabs} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
    Address,
    Client,
    EventResourceUse,
    formatAddress,
    FullWorkEvent, User, UserGroup, UserRoles,
    WorkOrder,
    WorkOrderActions,
    WorkOrderStatus
} from "../../types";
import {Navigate, useLocation, useParams} from "react-router-dom";
import React, {ChangeEvent, useEffect, useMemo, useRef, useState} from "react";
import {
    getClients,
    getDriverTimesheets,
    getEvents,
    getResourceUses,
    getTimesheets,
    getUsers,
    getWorkOrders,
    updateWorkOrder,
    uploadWorkOrderFiles
} from "../../api/dataService";
import Select from "react-select";
import StackedContentContainer from "../ContentContainer";
import {
    checkIsAllowed,
    encodeFilterString,
    getDateTimeString,
    getTotalEventHours,
    handleWorkOrderAction, isLoadingSpinner,
    renderQuoteNumber
} from "../../util/Helpers";
import AccordionHeader from "react-bootstrap/AccordionHeader";
import AccordionItem from "react-bootstrap/AccordionItem";
import AccordionBody from "react-bootstrap/AccordionBody";
import CreateEvent from "../CreateEvent";
import {ErrorMessage, Field, FieldProps, Formik} from "formik";
import Form from "react-bootstrap/Form";
import WorkOrderEventsTable from "./WorkOrderEventsTable";
import TimeEntriesTable from "./TimeEntriesTable";
import * as Yup from 'yup'
import DriverTimeEntriesTable from "./DriverTimeEntriesTable";
import {v4 as uuidv4} from "uuid";
import {fetchSafetySheet} from "../../api/api";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/reduxTypes";

    // TODO: Refactor Work Order  component to retrieve WorkOrder instances via a prop, rather navigate()
    // These two values are set when a user accesses this component using react navigate('/workorders/:id',{state: QuoteForm})
export const WorkOrderPage: React.FC = () =>  {
    const location = useLocation();
    const user = useSelector((state:RootState) => state.user);

    //Type Assertion - Assumes that the state object will always be an instance of WorkOrder
    const state = location.state as WorkOrder;

    // Current id of work order passed in from link
    const {id} = useParams();

    const [isLoading, setIsLoading] = useState(false)
    const formikRef = useRef<any>(null)
    const [workOrderData , setWorkOrderData] = useState<WorkOrder>();
    const [userList, setUserList] = useState<User[]>([])
    const [selectedSalesRep, setSelectedSalesRep] = useState<User | undefined>(undefined)
    const [poNumber, setPoNumber] = useState<string>('')
    const [eventData, setEventData] = useState<FullWorkEvent[]>([]);
    const [showEventModal, setShowEventModal] = useState(false)
    const [elevatorSchedule, setElevatorSchedule] = useState<string>()
    const [isDisabled,setIsDisabled] = useState<boolean>(false)
    const [elevatorScheduleFile, setElevatorScheduleFile] = useState<File>()
    const [safetyDataSheet, setSafetyDataSheet] = useState<string>()
    const [safetyDataSheetFile, setSafetyDataSheetFile] = useState<File>()
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [jobClient, setJobClient] = useState<Client>()
    const [showSuccess, setShowSuccess] = useState(false)
    const [saveError, setSaveError] = useState<string>('')
    const [billClient, setBillClient] = useState<Client>()

    // Tally of all hours spent on a Work Order across all event timesheet data
    const totalHoursWO = useMemo(() => {
        let total_hours = 0;
        eventData.forEach((eventData) => {
            total_hours = total_hours + getTotalEventHours(eventData.TimeSheetList) + getTotalEventHours(eventData.DriverTimeSheetList)
        })
        return total_hours
    }, [eventData]);

    useEffect(() => {
        getUsers().then(userResponse => {
            if (userResponse && Array.isArray(userResponse)) {
                setUserList(userResponse)
            }
        })
    }, [])

    useEffect(() => {
        if (workOrderData?.presented_by) {
            // PATCH responses return presented_by as a raw FK id, not a User object.
            // Only sync local state when we have a full User object (e.g. from a GET).
            const pb = workOrderData.presented_by as any
            if (pb && typeof pb === 'object' && 'first_name' in pb) {
                setSelectedSalesRep(pb as User)
            }
        }
        if (workOrderData?.po_number !== undefined) {
            setPoNumber(workOrderData.po_number)
        }
    }, [workOrderData])

    // Initialize Work Order data from the API whenever the id changes.
    //
    // NOTE: We intentionally do NOT rely on location.state as the source of truth
    // here. React Router v6 persists location.state in the browser's History API,
    // so it survives hard refreshes — meaning it will contain the *pre-save* version
    // of the work order after the user saves and refreshes, causing stale data to
    // appear. location.state is used only as an optimistic value for instant first
    // render while the API call is in flight.
    useEffect(() => {
        if (!id) {
            setWorkOrderData(state || undefined)
            return
        }
        // Optimistic instant render: show location.state immediately so the page
        // appears without a loading flash on first navigation from the list.
        if (state) {
            setWorkOrderData(state)
        }
        // Always fetch fresh data from the server to guarantee we show the latest
        // saved values (handles refresh, direct URL entry, and post-save state).
        Promise.all([getWorkOrders(id)])
            .then(([workOrderResponse]) => {
                if ('redirectTo' in workOrderResponse) {
                    return (<Navigate to={workOrderResponse.redirectTo}/>)
                }
                if (!Array.isArray(workOrderResponse) && workOrderResponse) {
                    setWorkOrderData(workOrderResponse)
                }
                setIsLoading(false)
            })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {

        // Work Order READ_ONLY permission logic
        if (state && (
            state.workorder_status === WorkOrderStatus.complete ||
            state.workorder_status === WorkOrderStatus.milestone
        )) {
            setIsDisabled(true);
        } else if (!checkIsAllowed(user.groups,[UserRoles.sales, UserRoles.management,UserRoles.ownership])) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }

    }, [state,id,user]);

    // Fetch clients and events when the work order loads or the work order ID changes.
    // Intentionally does NOT depend on the full workOrderData object — only on stable
    // identifiers — so that saving fields like PO number, notes, or sales rep does not
    // trigger an unnecessary re-fetch and loading-spinner flash that hides the updated UI.
    useEffect(() => {
        if (!workOrderData) return
        setEventData([])
        setIsLoading(true)
        Promise.all([
            getClients(undefined, `?client_name=${encodeFilterString(workOrderData.jobsite_client)}`),
            getClients(undefined, `?client_name=${encodeFilterString(workOrderData.billing_client)}`),
            getEvents(undefined, `?work_order=${id}`)
        ])
            .then(([jobClientResponse, billClientResponse, eventResponse]) => {

                if ('redirectTo' in jobClientResponse) { return (<Navigate to={jobClientResponse.redirectTo}/>) }
                if ('redirectTo' in billClientResponse) {return (<Navigate to={billClientResponse.redirectTo}/>) }

                if (Array.isArray(jobClientResponse) && Array.isArray(billClientResponse)) {
                    setJobClient(jobClientResponse[0]);
                    setBillClient(billClientResponse[0]);
                }

                // Set EventData Array which is an array of events with Resource, Timesheet, and Employee data attached
                if (Array.isArray(eventResponse)) {
                    // Create an array to store the promises for fetching timesheets and resource data
                    const promises = eventResponse.map((event) => {
                        return Promise.all([
                            getTimesheets(undefined, `?event=${event.id}`),
                            getDriverTimesheets(undefined,`?event=${event.id}`),
                            getResourceUses(undefined, `?event=${event.id}`)

                        ]);
                    });

                    // Execute all promises and wait for their completion
                    Promise.all(promises).then((results) => {
                        results.forEach(([timeSheetResponse,driverTimeSheetResponse, resourceResponse], index) => {
                            if ('redirectTo' in timeSheetResponse) { return (<Navigate to={timeSheetResponse.redirectTo}/>) }
                            if ('redirectTo' in driverTimeSheetResponse) { return (<Navigate to={driverTimeSheetResponse.redirectTo}/>) }
                            if ('redirectTo' in resourceResponse) { return (<Navigate to={resourceResponse.redirectTo}/>) }

                            const [eventTimeSheetData, eventDriverTimeSheetData,eventResourceData] = results[index];

                            if (Array.isArray(eventTimeSheetData) && Array.isArray(eventResourceData) && Array.isArray(eventDriverTimeSheetData) ) {

                                // Example of a single object to be inserted in the Event Data Array
                                let fullWorkEvent: FullWorkEvent = {
                                    WorkEvent : eventResponse[index],
                                    TimeSheetList : eventTimeSheetData,
                                    AssignedResourceList : eventResourceData,
                                    DriverTimeSheetList : eventDriverTimeSheetData,
                                    AssignedEmployeeList :  [] ,
                                }

                                setEventData(prevState => [...prevState, fullWorkEvent]);
                            }
                        });
                    });
                }
                setIsLoading(false)
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, workOrderData?.jobsite_client, workOrderData?.billing_client]);

    // Sync file-attachment paths whenever the work order's file fields change
    // (e.g. after a successful file upload that updates workOrderData).
    useEffect(() => {
        if (workOrderData?.safety_data_sheet) setSafetyDataSheet(workOrderData.safety_data_sheet)
        if (workOrderData?.elevator_schedule) setElevatorSchedule(workOrderData.elevator_schedule)
    }, [workOrderData?.safety_data_sheet, workOrderData?.elevator_schedule]);


    const handleEventDelete = (created_obj: any,  eventDataIndex: number) => {
        const updatedEventData = [...eventData]; // Create a copy of the array
        updatedEventData.splice(eventDataIndex, 1); // Remove the item at the specified index
        setEventData(updatedEventData);
        setShowEventModal(false)
    }

    const handleDismissUnsaved = () => {
        setPoNumber(workOrderData?.po_number ?? '')
        setSelectedSalesRep(workOrderData?.presented_by)
        setSafetyDataSheetFile(undefined)
        setElevatorScheduleFile(undefined)
        if (formikRef.current) formikRef.current.resetForm()
    }

    const handleSubmit = async ( values: Partial<WorkOrder>) => {
        // Create a FormData object to send the file to the server
        const formData = new FormData();
        const workOrderId = id || state?.id
        let didUpdate = false

        // Create a unique file name for s3
        if(values.safety_data_sheet && safetyDataSheetFile) {
            let shortRandomString = uuidv4().substring(0, 6);
            let uniqueFileName = `${Date.now()}_${shortRandomString}_${safetyDataSheetFile.name}`;
            formData.append('safety_data_sheet', safetyDataSheetFile, uniqueFileName);
        }

        // Create a unique file name for s3
        if(values.elevator_schedule && elevatorScheduleFile) {
            let shortRandomString = uuidv4().substring(0, 6);
            let uniqueFileName = `${Date.now()}_${shortRandomString}_${elevatorScheduleFile.name}`;
            formData.append('elevator_schedule', elevatorScheduleFile, uniqueFileName)
        }

        setSaveError('')
        try {
            if (workOrderId && (hasSalesRepChange || hasPONumberChange)) {
                const data: any = {}
                if (hasSalesRepChange && selectedSalesRep) data.presented_by = selectedSalesRep.id
                if (hasPONumberChange) data.po_number = poNumber
                const response = await updateWorkOrder(workOrderId, data)
                if (response && !Array.isArray(response) && !('redirectTo' in response)) {
                    // Use the server's response to update state so the UI always
                    // reflects what was actually persisted, not just local values.
                    setWorkOrderData(prev => prev ? { ...prev, ...(response as WorkOrder) } : prev)
                    didUpdate = true
                }
            }

            if (workOrderId && values.work_order_notes !== (workOrderData?.work_order_notes || '')) {
                const response = await updateWorkOrder(workOrderId, { work_order_notes: values.work_order_notes })
                if (response && !Array.isArray(response) && !('redirectTo' in response)) {
                    setWorkOrderData(prev => prev ? { ...prev, ...(response as WorkOrder) } : prev)
                    didUpdate = true
                }
            }

            if (workOrderId && (safetyDataSheetFile || elevatorScheduleFile)) {
                // Send the FormData to your server using a fetch or Axios
                const response = await uploadWorkOrderFiles(workOrderId, formData)

                if (response && !Array.isArray(response) && workOrderData) {
                    setShowSuccess(true)

                    // Type Assertion - assumes the response is a WorkOrder type
                    let responseData = response as WorkOrder

                    let updatedWorkOrderData = {...workOrderData};

                    // Update new file attachment paths if successfully added into the server(w/o needing to refresh window)
                    updatedWorkOrderData.safety_data_sheet = responseData.safety_data_sheet
                    updatedWorkOrderData.elevator_schedule = responseData.elevator_schedule
                    setWorkOrderData(updatedWorkOrderData)

                    // Reset form values
                    if(formikRef.current){ formikRef.current.resetForm() }
                    setElevatorSchedule(undefined)
                    setSafetyDataSheet(undefined)
                    didUpdate = true
                } else if (response && !Array.isArray(response)) {
                    // Handle server errors or display an error message
                    console.error('File upload failed:', response.statusText);
                }
            }
            if (didUpdate) {
                setShowSuccess(true)
            }
        } catch (error) {
            console.error('Save failed:', error)
            setSaveError('Failed to save changes. Please try again.')
        }
    };


    const initialValues: Partial<WorkOrder> =  {
            safety_data_sheet: '',
            elevator_schedule: '',
            work_order_notes: workOrderData?.work_order_notes || ''
    }

    const FILE_SIZE = 4 * 1024 * 1024; // 4 MB

    const SUPPORTED_FORMATS = [
        "image/jpg",
        "image/jpeg",
        "image/gif",
        "image/png",
        "image/heic",
        "application/pdf"
    ];

    // File validation ensures attached files follow correct formats and file size attributes
    const validationSchema = Yup.object().shape({
        safety_data_sheet: Yup
            .mixed<File>()
            .test(
                "blobOrValidation",
                "File too large or unsupported format",
                function (value) {
                    // Check if value is a blob (File object)
                    if (value instanceof File) {
                        return value.size <= FILE_SIZE && SUPPORTED_FORMATS.includes(value.type);
                    }
                    // If it's not a blob, it's not checked
                    return true;
                }
            ),
        elevator_schedule: Yup
            .mixed<File>()
            .test(
                "blobOrValidation",
                "File too large or unsupported format",
                function (value) {
                    // Check if value is a blob (File object)
                    if (value instanceof File) {
                        return value.size <= FILE_SIZE && SUPPORTED_FORMATS.includes(value.type);
                    }
                    // If it's not a blob, it's not checked
                    return true;
                }
            ),
    });

    // Resolve the stored presented_by to an id regardless of whether the PATCH
    // response returned a full User object or a raw FK id.
    const storedPresentedById = workOrderData?.presented_by
        ? (typeof (workOrderData.presented_by as any) === 'object'
            ? (workOrderData.presented_by as User).id
            : (workOrderData.presented_by as any))
        : null
    const hasSalesRepChange = (selectedSalesRep?.id ?? null) !== storedPresentedById
    const hasFileChanges = Boolean(safetyDataSheetFile || elevatorScheduleFile)
    const hasPONumberChange = poNumber !== (workOrderData?.po_number ?? '')
    const canSaveChanges = hasSalesRepChange || hasFileChanges || hasPONumberChange
    // PO number is editable for permitted users regardless of work order status
    const isPODisabled = !checkIsAllowed(user.groups, [UserRoles.sales, UserRoles.management, UserRoles.ownership])

    if (isLoading) {
        return (isLoadingSpinner());
    }

    return (
        workOrderData && eventData && billClient && jobClient ? (
            <>
            {(((hasSalesRepChange || hasFileChanges) && !isDisabled) || (hasPONumberChange && !isPODisabled)) && (
                <div
                    className="d-flex align-items-center justify-content-between px-4 py-2"
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1055,
                        backgroundColor: '#fff3cd',
                        borderBottom: '1px solid #ffc107',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}
                >
                    <span className="fw-semibold text-warning-emphasis">You have unsaved changes.</span>
                    <div className="d-flex gap-2">
                        <Button size="sm" variant="success" onClick={() => formikRef.current?.submitForm()}>Save</Button>
                        <Button size="sm" variant="outline-secondary" onClick={handleDismissUnsaved}>Dismiss</Button>
                    </div>
                </div>
            )}
            <StackedContentContainer title={`Work Order - #${workOrderData.id}`}>
            {showSuccess && (
                <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                    Changes saved successfully!
                </Alert>
            )}
            {saveError && (
                <Alert variant="danger" onClose={() => setSaveError('')} dismissible>
                    {saveError}
                </Alert>
            )}
            <div>
                <div className="d-flex align-items-center gap-3 mb-2">
                    <div className="text-nowrap">Status - {workOrderData.workorder_status}</div>
                    <div className="text-nowrap">
                        Originating Quote - {renderQuoteNumber(workOrderData.origin_quote, user.groups, 'N/A')}
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center gap-2" style={{ flex: '1 1 0' }}>
                        <div className="fw-semibold text-nowrap">PO Number -</div>
                        <Form.Control
                            value={poNumber}
                            onChange={(e) => setPoNumber(e.target.value)}
                            disabled={isPODisabled}
                            placeholder="PO Number"
                            style={{ maxWidth: 200 }}
                        />
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-2" style={{ flex: '1 1 0' }}>
                        <div className="fw-semibold text-nowrap">Sales Representative -</div>
                        <Select
                            name="presented_by"
                            className="w-auto"
                            value={selectedSalesRep ? {
                                label: `${selectedSalesRep.last_name}, ${selectedSalesRep.first_name}`,
                                value: selectedSalesRep
                            } : null}
                            options={userList
                                .filter(u =>
                                    Array.isArray(u.groups) &&
                                    (u.groups as Array<UserGroup | string>).some((g: UserGroup | string) =>
                                        typeof g === "string"
                                            ? g.toLowerCase() === "sales"
                                            : (g.name ?? "").toLowerCase() === "sales"
                                    )
                                )
                                .map(entry => ({
                                    label: `${entry.last_name}, ${entry.first_name}`,
                                    value: entry
                                }))
                                .sort((a, b) => a.label.localeCompare(b.label))}
                            onChange={(selectedOptions) => {
                                if (selectedOptions && (id || state?.id)) {
                                    setSelectedSalesRep(selectedOptions.value)
                                }
                            }}
                            isDisabled={isDisabled}
                        />
                    </div>
                    <div className="d-flex justify-content-end" style={{ flex: '1 1 0' }}>
                        {
                            state ?
                                <Button
                                onClick={() => handleWorkOrderAction(state, WorkOrderActions.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}>
                                Download PDF
                            </Button> : ''
                        }
                    </div>
                </div>
            </div>

                <hr/>

                <p className="fw-normal text-center">Client Summary</p>

                <Row>
                    <Col className="d-flex justify-content-center">
                        <div>
                            <Row>
                                <p><span className="fw-bold">Client: </span>{jobClient?.client_name}</p>
                            </Row>
                            <Row>
                                <p>{jobClient?.address ? formatAddress(jobClient.address as Address) : "No Address"}</p>
                            </Row>
                            <Row>
                                <p><span className="fw-bold">Contact Name: </span>{jobClient?.contact.contact_name}</p>
                            </Row>
                            <Row>
                                <p><span className="fw-bold">Phone: </span>{jobClient?.contact.contact_phone_number}</p>
                            </Row>
                        </div>
                    </Col>

                    <Col className="d-flex justify-content-center">
                        <div>
                            <Row>
                                <p><span className="fw-bold">Bill to: </span>{billClient?.client_name}</p>
                            </Row>
                            <Row>
                                <p>{jobClient?.address ? formatAddress(billClient?.address as Address) : "No Address"}</p>
                            </Row>
                            <Row>
                                <p><span className="fw-bold">Contact Name: </span>{billClient?.contact.contact_name}</p>
                            </Row>
                            <Row>
                                <p><span className="fw-bold">Phone: </span>{billClient?.contact.contact_phone_number}</p>
                            </Row>
                        </div>
                    </Col>
                </Row>
                <hr/>
                <p className="fw-normal text-center">Event Details</p>
                <div className="container" >
                <WorkOrderEventsTable disabled={isDisabled} eventData={eventData} handleEventDelete={handleEventDelete} />
                    <Button disabled={isDisabled} onClick={()=> {
                        setShowEventModal(true)
                    }} variant="success">Add Event</Button>
                </div>


                <hr/>
                <p className="fw-normal text-center">Event Time Sheets</p>
                <Accordion alwaysOpen={true} defaultActiveKey={"0"}>
                {
                    eventData.map((event) => {
                        return (
                        <AccordionItem eventKey={event.WorkEvent.id ? event.WorkEvent.id : "0"}>
                            <AccordionHeader>
                                <Col>Timesheet - {event.WorkEvent.event_name}</Col>
                            </AccordionHeader>
                            <AccordionBody>

                                <Row><p className={"text-center"}>{getDateTimeString(event.WorkEvent.start_date)} - {getDateTimeString(event.WorkEvent.end_date)}</p></Row>
                                <Tabs defaultActiveKey={"generalLabour"}>
                                    <Tab eventKey={"generalLabour"} title={"General Labour Time Cards"}>
                                        <TimeEntriesTable event_id={event.WorkEvent.id ? event.WorkEvent.id : "Unknown Event"} timeSheetData={event.TimeSheetList}/>
                                    </Tab>
                                    <Tab eventKey={"driver"} title={"Driver Time Cards"}>
                                        <DriverTimeEntriesTable timeSheetData={event.DriverTimeSheetList} event_id={event.WorkEvent.id}/>
                                    </Tab>
                                </Tabs>
                                <p>Total General Labour Hours: <span>{getTotalEventHours(event.TimeSheetList)}</span></p>
                                <p>Total Driver Hours: <span>{getTotalEventHours(event.DriverTimeSheetList)}</span></p>

                            </AccordionBody>
                        </AccordionItem>
                        )})
                }
                </Accordion>

                <Col>
                    <p><span className={"fw-bold"}>Total Worked Hours (Driving + Labour): </span>{totalHoursWO}</p>
                </Col>
                <hr/>

                <p className={"text-center"}>Work Order Notes</p>
                <div>
                    <Formik innerRef={formikRef} enableReinitialize={true} validationSchema={validationSchema} initialValues={initialValues} onSubmit={handleSubmit}>
                        {
                            (formik) => (
                                <Form onSubmit={formik.handleSubmit} autoComplete="off">
                                    <Row className="mb-3">
                                        <Col>
                                            <Field
                                                disabled={isDisabled}
                                                name="work_order_notes"
                                                as={FormControl}
                                                onChange={formik.handleChange}
                                            />
                                        </Col>
                                    </Row>

                                    <p className={"text-center"}>Additional File Attachments</p>
                                    <Row>
                                        <Col>
                                            {/*Retrieves a Safety Sheet information*/}
                                            <Button onClick = {() => {
                                                fetchSafetySheet(id)
                                            }} disabled={isDisabled}>Generate WO Safety Sheet PDF </Button>
                                        </Col>
                                        <Col>
                                            <FormGroup className="mb-5" controlId="safetyDataSheet">
                                                <p className="fw-normal">Signed WO Safety Sheet</p>

                                                {
                                                    safetyDataSheet ?
                                                        <div>
                                                        <a
                                                            href={safetyDataSheet}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {safetyDataSheet}
                                                        </a>
                                                    </div> : ""
                                                }
                                                <Field name="safety_data_sheet" type="file">
                                                    {({ field, form }:FieldProps<File>) => (
                                                        <div>
                                                            <input
                                                                name="safety_data_sheet"
                                                                type="file"
                                                                disabled={isDisabled}
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                                    setShowSuccess(false);
                                                                    setErrorMessages([]);
                                                                    form.setFieldTouched("safety_data_sheet", true, true);
                                                                    if (event.currentTarget.files) {
                                                                        form.setFieldValue("safety_data_sheet", event.currentTarget.files[0]);
                                                                        setSafetyDataSheetFile(event.currentTarget.files[0])
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </Field>
                                                <ErrorMessage name="safety_data_sheet" component="div" className="text-danger" />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col></Col>
                                        <Col>
                                            <FormGroup className="mb-5" controlId="elevatorSchedule">
                                                <p className="fw-normal">Elevator Schedule</p>

                                                {
                                                    elevatorSchedule ?
                                                        <div>
                                                            <a
                                                                href={elevatorSchedule}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {elevatorSchedule}
                                                            </a>
                                                        </div> : ""
                                                }

                                                <Field name="elevator_schedule" type="file">
                                                    {({ field, form }:FieldProps<File>) => (
                                                        <div>
                                                            <input
                                                                disabled={isDisabled}
                                                                type="file"
                                                                name="elevator_schedule"
                                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                                    setShowSuccess(false);
                                                                    setErrorMessages([]);
                                                                    form.setFieldTouched("elevator_schedule", true, true);

                                                                    if (event.currentTarget.files) {
                                                                        form.setFieldValue("elevator_schedule", event.currentTarget.files[0]);
                                                                        setElevatorScheduleFile(event.currentTarget.files[0])

                                                                    } else {
                                                                        form.setFieldValue("elevator_schedule", null); // or set it to an empty string ""
                                                                    }}}
                                                            />
                                                        </div>
                                                    )}
                                                </Field>
                                                <ErrorMessage name="elevator_schedule" component="div" className="text-danger" />

                                            </FormGroup>
                                        </Col>

                                    </Row>
                                    <Row className="p-lg-5">

                                        {errorMessages.length > 0 && (
                                        <Alert variant="danger">
                                            <ul>
                                                {errorMessages.map((message, index) => (
                                                    <li key={index}>{message}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                    <Button onClick={() => {
                                        if (formik.errors && Object.keys(formik.errors).length > 0) {
                                            Object.entries(formik.errors).forEach(([key, value]) => {
                                                setErrorMessages(prevState => [...prevState, `${key}: ${value}`]);
                                            });
                                        }
                                    }} type="submit" disabled={!(canSaveChanges || formik.dirty) || isDisabled}>
                                        Save
                                    </Button>
                                    </Row>
                                </Form>
                            )
                        }
                    </Formik>
                </div>

                {/*MODALS / POP UP WINDOWS */}

                <Modal show={showEventModal} onHide={() => {
                    setShowEventModal(false)
                }} size={"lg"} backdrop="static" keyboard={false}>
                    <Modal.Header>
                        <Modal.Title>{`Create an Event`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <CreateEvent
                            showCrew={true}
                            isQuoting={false}
                            submitEvent={true}
                            workOrder={id}
                            closeModal={() => {
                                setShowEventModal(false)
                            }}

                            onCreate=
                                {(createdObject,
                                  assignedEmployeeList,
                                  assignedResource: EventResourceUse[]) => {

                                    // Update Work Event table when a new event is added (w/o a page refresh)
                                    let fullWorkEvent: FullWorkEvent = {
                                        WorkEvent : createdObject,
                                        AssignedEmployeeList : assignedEmployeeList,
                                        AssignedResourceList : assignedResource,
                                        TimeSheetList : [],
                                        DriverTimeSheetList : []
                                    }
                                    setEventData(prevState => [...prevState, fullWorkEvent]);
                                    setShowEventModal(false)
                            }}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {
                            setShowEventModal(false)
                        }}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </StackedContentContainer>
            </>
             ) : (
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        )


    );
}

export default WorkOrderPage;
